import { useSyncExternalStore } from "react";

/**
 * Client-configurable SDMS Result Validation Rollback UI (Settings → Default
 * Settings → SDMS Rollback).
 *
 *   • 'classic'  — the legacy reason-only "Raw Data Batch Rollback" popup.
 *   • 'enhanced' — the typed roll-back window (Correction / Re-analysis / Re-prep
 *                  with the scoped sample × parameter selection).
 *
 * Loaded once after login from `GET /api/v1/app-config` (AppShell) and applied
 * app-wide; the SDMS `RollbackModal` reads it to decide which body to render.
 * Mirrors the loader / progress-bar client-config pattern (CLAUDE.md §18.6) and
 * the module-level setting store already used in `utils/datetime.ts`.
 *
 * Default is 'classic' so existing behaviour is preserved until an admin opts in.
 */
export type SdmsRollbackUi = "classic" | "enhanced";

let current: SdmsRollbackUi = "classic";
const listeners = new Set<() => void>();

function normalize(v: string | null | undefined): SdmsRollbackUi {
  return v === "enhanced" ? "enhanced" : "classic";
}

/** Set the app-wide SDMS rollback UI mode (from app-config, or a live edit). */
export function setSdmsRollbackUi(v: string | null | undefined): void {
  const next = normalize(v);
  if (next === current) return;
  current = next;
  for (const l of listeners) l();
}

/** The current app-wide mode (non-reactive read). */
export function getSdmsRollbackUi(): SdmsRollbackUi {
  return current;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Reactive hook — re-renders the caller when the mode changes. */
export function useSdmsRollbackUi(): SdmsRollbackUi {
  return useSyncExternalStore(subscribe, getSdmsRollbackUi, getSdmsRollbackUi);
}
